import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

type Organization = {
  id: string;
  name: string;
  logo?: {
    id: string;
    url: string;
  } | null;
};

interface CopyToOrganizationDialogProps {
  open: boolean;
  courseId: string;
  courseName: string;
  organizations: Organization[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (organizationId: string, name: string) => Promise<void>;
}

const CopyToOrganizationDialog: React.FC<CopyToOrganizationDialogProps> = ({
  open,
  courseId,
  courseName,
  organizations,
  loading,
  onClose,
  onSubmit,
}) => {
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>("");
  const [newCourseName, setNewCourseName] = useState<string>(courseName);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Reset states when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedOrganizationId(organizations?.[0]?.id || "");
      setNewCourseName(courseName);
      setIsSubmitting(false);
    }
  }, [open, courseName, organizations]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(selectedOrganizationId, newCourseName);
    } catch (error) {
      console.error("Error copying course to organization:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Course to Organization</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : organizations.length === 0 ? (
          <Typography color="error">
            No organizations available for copying. You may not have permission
            to create courses in other organizations.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select the organization where you want to copy this course. The
              course content will be copied without student data, roles, or
              media files.
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="organization-select-label">
                Target Organization
              </InputLabel>
              <Select
                labelId="organization-select-label"
                value={selectedOrganizationId}
                label="Target Organization"
                onChange={(e) => setSelectedOrganizationId(e.target.value)}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Course Name"
              value={newCourseName}
              onChange={(e) => setNewCourseName(e.target.value)}
              fullWidth
              required
              error={!newCourseName.trim()}
              helperText={
                !newCourseName.trim() ? "Course name is required" : ""
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            isSubmitting ||
            loading ||
            organizations.length === 0 ||
            !selectedOrganizationId ||
            !newCourseName.trim()
          }
        >
          {isSubmitting ? <CircularProgress size={24} /> : "Copy Course"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyToOrganizationDialog;
